import {
  ApolloError,
  DocumentNode,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  TypedDocumentNode,
  useMutation as apolloUseMutation
} from "@apollo/client";
import * as Sentry from "@sentry/react";
import { GraphQLError } from "graphql";
import { useSnackbar } from "notistack";
import { useCallback } from "react";

type AdditionalOptions = {
  onErrorSnackbarMessage?: string;
};

export class GraphQLMutationError extends Error {
  errors: ReadonlyArray<GraphQLError>;
  constructor(errors: ReadonlyArray<GraphQLError>) {
    super(errors.map(e => e.message).join("; "));
    this.errors = errors;
  }
}

function extractErrorArray(e: unknown): readonly GraphQLError[] {
  if (e instanceof GraphQLMutationError) {
    return e.errors;
  }

  if (e instanceof ApolloError) {
    const networkError = e.networkError as any;
    if (networkError?.result?.errors) {
      return networkError.result.errors;
    }

    if (e.graphQLErrors) {
      return e.graphQLErrors;
    }
  }

  return [e as any];
}

/**
 * A wrapper around Apollo's useMutation hook. This wrapper:
 *
 * - Always returns an "errors" property when there's an error, whether it's a
 *   network-level error (which get thrown by Apollo's useMutation), or a
 *   GraphQL error returned by the backend (which get returned in a `errors`
 *   property by Apollo's useMutation).
 *
 * - Adds a onErrorSnackbarMessage option to automatically show an error
 *   snackbar when there's an error.
 */
export default function useMutation<
  TData = any,
  TVariables = OperationVariables
>(
  // These types are copied from the type of Apollo's useMutation()
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables> & AdditionalOptions
): MutationTuple<TData, TVariables> {
  const { enqueueSnackbar } = useSnackbar();

  const [fn, result] = apolloUseMutation(mutation, options);

  const wrappedMutation = useCallback(
    async (...args: Parameters<typeof fn>) => {
      try {
        const result = await fn(...args);
        if (result.errors) {
          throw new GraphQLMutationError(result.errors);
        }

        return result;
      } catch (e) {
        console.error(e);

        if (options?.onErrorSnackbarMessage) {
          enqueueSnackbar(options.onErrorSnackbarMessage, { variant: "error" });
        }

        const errors = extractErrorArray(e);
        Sentry.captureException(errors[0], {
          extra: {
            otherErrors: errors.slice(1)
          }
        });

        return { errors };
      }
    },
    [enqueueSnackbar, fn, options?.onErrorSnackbarMessage]
  );

  return [wrappedMutation, result];
}
